import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Check, PlayCircle, Trash2 } from 'lucide-react';
import axios from 'axios';
import BottlingPlanToolbar from '../components/toolbars/BottlingPlanListToolbar';

const BottlingPlansList = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFinalizeModal, setShowFinalizeModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [finalCases, setFinalCases] = useState({});
    const [finalizeLoading, setFinalizeLoading] = useState(false);
    
    const navigate = useNavigate();

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/bottling-plans');
            setPlans(response.data);
        } catch (err) {
            setError('Failed to fetch bottling plans');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (planId, newStatus, e) => {
        e.stopPropagation();
        try {
            await axios.put(`/api/bottling-plans/${planId}/status`, {
                status: newStatus
            });
            fetchPlans();
        } catch (err) {
            setError('Failed to update plan status');
        }
    };

    const handleShowFinalize = async (planId, e) => {
        e.stopPropagation();
        try {
            const response = await axios.get(`/api/bottling-plans/${planId}`);
            setSelectedPlan(response.data);
            const initialFinalCases = {};
            response.data.items.forEach(item => {
                initialFinalCases[item.itemID] = item.casesEstimated;
            });
            setFinalCases(initialFinalCases);
            setShowFinalizeModal(true);
        } catch (err) {
            setError('Failed to load plan details for finalization');
        }
    };

    const handleFinalizeConfirm = async () => {
        if (!selectedPlan) return;

        try {
            setFinalizeLoading(true);
            await axios.post(`/api/bottling-plans/${selectedPlan.planID}/finalize`, {
                items: Object.entries(finalCases).map(([itemId, cases]) => ({
                    itemId: parseInt(itemId),
                    finalCases: parseInt(cases)
                }))
            });
            setShowFinalizeModal(false);
            fetchPlans();
        } catch (err) {
            setError('Failed to finalize bottling plan');
        } finally {
            setFinalizeLoading(false);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Draft': return 'bg-warning';
            case 'Scheduled': return 'bg-info';
            case 'Completed': return 'bg-success';
            default: return 'bg-secondary';
        }
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <BottlingPlanToolbar 
                onAddNew={() => navigate('/bottling-plans/new')}
            />

            <div className="container mt-4">
                {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {error}
                        <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Plan Date</th>
                                        <th>Status</th>
                                        <th>Bottling Date</th>
                                        <th>Performed By</th>
                                        <th>Total Cases</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {plans.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center">No bottling plans found</td>
                                        </tr>
                                    ) : (
                                        plans.map((plan) => (
                                            <tr 
                                                key={plan.planID}
                                                onClick={() => navigate(`/bottling-plans/${plan.planID}`)}
                                                className="table-row-hover"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td>{new Date(plan.planDate).toLocaleDateString()}</td>
                                                <td>
                                                    <span className={`badge ${getStatusBadgeClass(plan.status)}`}>
                                                        {plan.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    {plan.bottlingDate ? 
                                                        new Date(plan.bottlingDate).toLocaleDateString() : 
                                                        'Not set'}
                                                </td>
                                                <td>{plan.performedBy || 'Not assigned'}</td>
                                                <td>{plan.totalCases || 0}</td>
                                                <td>
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/bottling-plans/${plan.planID}`);
                                                            }}
                                                            title="Edit"
                                                        >
                                                            <Edit2 size={18} />
                                                        </button>
                                                        {plan.status === 'Draft' && (
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={(e) => handleStatusChange(plan.planID, 'Scheduled', e)}
                                                                title="Schedule"
                                                            >
                                                                <PlayCircle size={18} />
                                                            </button>
                                                        )}
                                                        {plan.status === 'Scheduled' && (
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={(e) => handleShowFinalize(plan.planID, e)}
                                                                title="Finalize"
                                                            >
                                                                <Check size={18} />
                                                            </button>
                                                        )}
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (window.confirm('Are you sure you want to delete this plan?')) {
                                                                    // Handle delete
                                                                }
                                                            }}
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Finalize Modal */}
                {showFinalizeModal && selectedPlan && (
                    <>
                        <div className="modal fade show d-block" tabIndex="-1">
                            <div className="modal-dialog modal-lg">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Finalize Bottling Plan</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setShowFinalizeModal(false)}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <p className="mb-3">
                                            Please confirm the final case counts for each item. 
                                            These will be used to create initial inventory records.
                                        </p>
                                        <div className="table-responsive">
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th>Wine</th>
                                                        <th>Est. Cases</th>
                                                        <th>Final Cases</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedPlan.items.map((item) => (
                                                        <tr key={item.itemID}>
                                                            <td>
                                                                {item.vintage} {item.varietal} {item.fanciful}
                                                            </td>
                                                            <td>{item.casesEstimated}</td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={finalCases[item.itemID]}
                                                                    onChange={(e) => setFinalCases({
                                                                        ...finalCases,
                                                                        [item.itemID]: e.target.value
                                                                    })}
                                                                    min="0"
                                                                    required
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setShowFinalizeModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleFinalizeConfirm}
                                            disabled={finalizeLoading}
                                        >
                                            {finalizeLoading ? 'Finalizing...' : 'Finalize Plan'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show"></div>
                    </>
                )}

                <style>{`
                    .table-row-hover:hover td {
                        background-color: rgba(0,0,0,.075);
                    }
                    .table-row-hover {
                        transition: background-color 0.2s ease;
                    }
                    .btn svg {
                        vertical-align: middle;
                        margin-top: -2px;
                    }
                    .btn:hover svg {
                        transform: scale(1.1);
                        transition: transform 0.2s ease;
                    }
                `}</style>
            </div>
        </>
    );
};

export default BottlingPlansList;